/**
 * Utilities for handling extension downloads with proper error handling.
 *
 * When presigned URLs expire, users need clear instructions to refresh and retry.
 */

import { DownloadErrorType, DOWNLOAD_ERROR_MESSAGES } from "./types";

/**
 * Determine the type of download error from an error object or response.
 */
export function getDownloadErrorType(error: unknown): DownloadErrorType {
  if (!error) return "unknown";

  // Check for network errors
  if (error instanceof TypeError && error.message?.includes("fetch")) {
    return "network_error";
  }

  // Check for fetch Response object
  if (error instanceof Response) {
    if (error.status === 403) {
      // S3 returns 403 for expired presigned URLs
      return "url_expired";
    }
    if (error.status === 401) {
      return "access_denied";
    }
  }

  // Check error message patterns (for wrapped errors)
  const message =
    typeof error === "object" && error !== null
      ? (error as Error).message?.toLowerCase() || ""
      : String(error).toLowerCase();

  if (
    message.includes("expired") ||
    message.includes("request has expired") ||
    message.includes("signature expired")
  ) {
    return "url_expired";
  }

  if (
    message.includes("accessdenied") ||
    message.includes("access denied") ||
    message.includes("forbidden")
  ) {
    return "url_expired"; // S3 often returns AccessDenied for expired URLs
  }

  if (
    message.includes("network") ||
    message.includes("failed to fetch") ||
    message.includes("networkerror")
  ) {
    return "network_error";
  }

  return "unknown";
}

/**
 * Get a user-friendly error message for a download error.
 */
export function getDownloadErrorMessage(error: unknown): string {
  const errorType = getDownloadErrorType(error);
  return DOWNLOAD_ERROR_MESSAGES[errorType];
}

/**
 * Check if an error indicates the presigned URL has expired.
 */
export function isUrlExpiredError(error: unknown): boolean {
  return getDownloadErrorType(error) === "url_expired";
}

/**
 * Attempt to start a download and handle common errors.
 *
 * @param url - The presigned download URL
 * @param filename - Optional filename for the download
 * @returns Promise that resolves on success, rejects with typed error on failure
 */
export async function startDownload(
  url: string,
  filename?: string
): Promise<void> {
  console.info("[Extension Download] Starting download", {
    hasUrl: !!url,
    filename: filename || "extension.zip",
  });

  if (!url) {
    console.error("[Extension Download] Download URL is empty");
    throw new Error(
      "Download URL is empty. Please refresh the page and try again."
    );
  }

  // Trigger the download directly using an anchor element
  // This bypasses CORS issues that would occur with fetch/HEAD requests
  // The browser handles the download natively, and presigned URLs work fine
  // Note: If the URL is expired, the user will see an S3 error page after clicking
  // This is acceptable UX - they'll know to come back and refresh
  const link = document.createElement("a");
  link.href = url;
  link.download = filename || "extension.zip";
  link.style.display = "none";
  document.body.appendChild(link);

  console.info("[Extension Download] Triggering download via anchor click");
  link.click();

  // Small delay before removing to ensure click registers
  setTimeout(() => {
    document.body.removeChild(link);
  }, 100);

  console.info("[Extension Download] Download initiated");
}
